/**
 * Automatically recalculates cd/ca (day change departure/arrival) values
 * whenever station times or UTC offsets change.
 */

import { useEffect, useMemo } from 'react';
import { calculateAllDayChanges } from '../utils/dayChangeCalculator';
import type { StationSector } from '../utils/types';

interface UseDayChangeCalculatorProps {
  stations: StationSector[];
  onUpdate: (stations: StationSector[]) => void;
  enabled?: boolean;
}

export function useDayChangeCalculator({
  stations,
  onUpdate,
  enabled = true,
}: UseDayChangeCalculatorProps) {
  // Stable key that only changes when time-related fields change
  const timeKey = useMemo(
    () => stations.map(s =>
      `${s.std}|${s.sta}|${s.utcLocalTimeVariationDep}|${s.utcLocalTimeVariationArr}`
    ).join(';'),
    [stations]
  );

  useEffect(() => {
    if (!enabled || !stations || stations.length === 0) return;

    const stationsCopy = structuredClone(stations);
    calculateAllDayChanges(stationsCopy);

    const hasChanges = stations.some((station, index) =>
      station.cd !== stationsCopy[index].cd || station.ca !== stationsCopy[index].ca
    );

    if (hasChanges) {
      onUpdate(stationsCopy);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timeKey, enabled]);
}
